import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { CorrectionRequestsAdmin } from '../components/CorrectionRequestsAdmin';
import MasterDirectory from '../components/MasterDirectory';
import DepartmentsManager from '../components/DepartmentsManager';
import SystemConfig from '../components/SystemConfig';
import RoleManagement from './RoleManagement';
import DataIntegrity from './DataIntegrity';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Shield, Users, Building2, Trash2, Edit, Key, Database,
  RefreshCw, Loader2, AlertTriangle, Search, Check, X,
  Plus, ChevronDown, ChevronRight, Eye, Download, FileText, Camera, ShieldCheck, UserX, Settings
} from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE as API_URL } from '../lib/apiBase';
const h = () => {
  const t = localStorage.getItem('hrms-token');
  return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

/* ====================================================================
   DATA MANAGEMENT TAB
   ==================================================================== */
function DataManagement() {
  const [clearing, setClearing] = useState(false);
  const [result, setResult] = useState(null);

  const clearAll = async () => {
    if (!window.confirm('This will PERMANENTLY DELETE all data. Super Admin account will remain. Continue?')) return;
    setClearing(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/clear-all-data`, { method: 'POST', headers: h(), credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setResult({ type: 'clear', data });
      toast.success('All data cleared');
    } catch (e) { toast.error(e.message); }
    finally { setClearing(false); }
  };

  return (
    <div className="space-y-6" data-testid="data-management-tab">
      <div className="max-w-md">
        <Card className="border-red-200 bg-red-50/30">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2 text-red-700">
              <Trash2 className="w-4 h-4" /> Clear All Data
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-4">Permanently remove all records from the system. Super Admin account will be preserved.</p>
            <Button onClick={clearAll} disabled={clearing} variant="destructive" className="w-full" data-testid="clear-all-btn">
              {clearing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Clear All Data
            </Button>
          </CardContent>
        </Card>
      </div>

      {result?.type === 'clear' && (
        <Card className="border-green-200">
          <CardContent className="p-4">
            <p className="text-sm text-green-700 font-medium">Data cleared successfully. System reset complete.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ====================================================================
   USER MANAGEMENT TAB (with password reset)
   ==================================================================== */
function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [resetDialog, setResetDialog] = useState(null);
  const [editDialog, setEditDialog] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [editFields, setEditFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState([]);
  const [addDialog, setAddDialog] = useState(false);
  const [addFields, setAddFields] = useState({ name: '', email: '', password: '', role: 'employee', entity_id: '', department: '' });
  const [entities, setEntities] = useState([]);
  const [entityFilter, setEntityFilter] = useState('all');
  const [creatingCreds, setCreatingCreds] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/users`, { headers: h(), credentials: 'include' });
      if (res.ok) setUsers(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const fetchEntities = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/entities`, { headers: h(), credentials: 'include' });
      if (res.ok) setEntities(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchUsers(); fetchEntities(); }, [fetchUsers, fetchEntities]);

  const handleAddUser = async () => {
    if (!addFields.name || !addFields.email || !addFields.password) { toast.error('Name, email, and password are required'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/create-user`, {
        method: 'POST', headers: h(), credentials: 'include',
        body: JSON.stringify(addFields),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
      toast.success(`User ${addFields.email} created successfully`);
      setAddDialog(false);
      setAddFields({ name: '', email: '', password: '', role: 'employee', entity_id: '', department: '' });
      fetchUsers();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleDownloadCredentials = async (format) => {
    try {
      const entityParam = entityFilter !== 'all' ? `?entity_id=${entityFilter}` : '';
      const res = await fetch(`${API_URL}/api/admin/employee-credentials/${format}${entityParam}`, { headers: h(), credentials: 'include' });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = format === 'pdf' ? 'SGI_HRMS_Employee_Credentials.pdf' : 'SGI_HRMS_Employee_Credentials.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Credentials ${format.toUpperCase()} downloaded`);
    } catch (e) { toast.error(e.message); }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 4) { toast.error('Password must be at least 4 characters'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/reset-password`, {
        method: 'POST', headers: h(), credentials: 'include',
        body: JSON.stringify({ user_id: resetDialog.user_id, new_password: newPassword }),
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(`Password reset for ${resetDialog.email}`);
      setResetDialog(null); setNewPassword('');
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleEditUser = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/records/users/${editDialog.user_id}`, {
        method: 'PUT', headers: h(), credentials: 'include',
        body: JSON.stringify(editFields),
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('User updated'); setEditDialog(null); fetchUsers();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Delete user ${user.email}?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/records/users/${user.user_id}`, { method: 'DELETE', headers: h(), credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      toast.success('User deleted'); fetchUsers();
    } catch (e) { toast.error(e.message); }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.length} users?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/bulk-delete`, {
        method: 'POST', headers: h(), credentials: 'include',
        body: JSON.stringify({ collection: 'users', record_ids: selected }),
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(`Deleted ${selected.length} users`); setSelected([]); fetchUsers();
    } catch (e) { toast.error(e.message); }
  };

  const handleCreateCredentials = async () => {
    if (!window.confirm('This will create login accounts for all employees who don\'t have one yet. Default passwords will be generated. Continue?')) return;
    setCreatingCreds(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/bulk-create-employee-accounts`, {
        method: 'POST', headers: h(), credentials: 'include',
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
      const data = await res.json();
      toast.success(`Created ${data.created} accounts, ${data.skipped} already existed`);
      fetchUsers();
    } catch (e) { toast.error(e.message); }
    finally { setCreatingCreds(false); }
  };

  const openEdit = (user) => {
    setEditFields({ name: user.name || '', email: user.email, role: user.role, entity_id: user.entity_id || '', department: user.department || '', is_active: user.is_active });
    setEditDialog(user);
  };

  const toggleSelect = (uid) => setSelected(prev => prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]);
  const toggleAll = () => setSelected(prev => prev.length === filtered.length ? [] : filtered.map(u => u.user_id));

  const filtered = users.filter(u => {
    const matchSearch = !search || u.email?.toLowerCase().includes(search.toLowerCase()) || u.name?.toLowerCase().includes(search.toLowerCase()) || u.role?.toLowerCase().includes(search.toLowerCase());
    const matchEntity = entityFilter === 'all' || u.entity_id === entityFilter;
    return matchSearch && matchEntity;
  });

  return (
    <div className="space-y-4" data-testid="user-management-tab">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex gap-2 flex-1 min-w-0">
          <div className="relative flex-1 min-w-[150px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search users..." className="pl-10 rounded-sm" value={search}
              onChange={e => setSearch(e.target.value)} data-testid="user-search" />
          </div>
          <Select value={entityFilter} onValueChange={setEntityFilter}>
            <SelectTrigger className="w-[180px]" data-testid="entity-filter-select">
              <Building2 className="w-4 h-4 mr-1 text-slate-400" />
              <SelectValue placeholder="All Institutes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Institutes</SelectItem>
              {entities.map(ent => (
                <SelectItem key={ent.entity_id} value={ent.entity_id}>{ent.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2 flex-wrap">
          {selected.length > 0 && (
            <Button variant="destructive" size="sm" onClick={handleBulkDelete} data-testid="bulk-delete-users-btn">
              <Trash2 className="w-3 h-3 mr-1" /> Delete {selected.length}
            </Button>
          )}
          <Button size="sm" variant="outline" className="border-green-300 text-green-700 hover:bg-green-50" onClick={handleCreateCredentials} disabled={creatingCreds} data-testid="create-credentials-btn">
            {creatingCreds ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Key className="w-3 h-3 mr-1" />}
            {creatingCreds ? 'Creating...' : 'Create Credentials'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleDownloadCredentials('pdf')} data-testid="download-credentials-pdf-btn">
            <FileText className="w-3 h-3 mr-1" /> PDF
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleDownloadCredentials('excel')} data-testid="download-credentials-csv-btn">
            <Download className="w-3 h-3 mr-1" /> CSV
          </Button>
          <Button onClick={() => { setAddFields({ name: '', email: '', password: '', role: 'employee', entity_id: '', department: '' }); setAddDialog(true); }}
            className="bg-[#DC2626] hover:bg-[#B91C1C] text-white gap-2" data-testid="add-user-btn">
            <Plus className="w-4 h-4" /> Add User
          </Button>
        </div>
      </div>

      <Card className="border border-slate-200 rounded-sm shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />)}</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-10"><input type="checkbox" checked={selected.length === filtered.length && filtered.length > 0} onChange={toggleAll} /></TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500">Name / Email</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500">Role</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500">Status</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 w-36">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(u => (
                    <TableRow key={u.user_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.user_id}`}>
                      <TableCell><input type="checkbox" checked={selected.includes(u.user_id)} onChange={() => toggleSelect(u.user_id)} /></TableCell>
                      <TableCell>
                        <p className="text-sm font-medium">{u.name || u.email}</p>
                        <p className="text-xs text-slate-400 font-mono">{u.email}</p>
                      </TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{u.role}</Badge></TableCell>
                      <TableCell>
                        <Badge className={u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`edit-user-${u.user_id}`}>
                            <Edit className="w-3.5 h-3.5 text-blue-500" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => { setResetDialog(u); setNewPassword(''); }} data-testid={`reset-pwd-${u.user_id}`}>
                            <Key className="w-3.5 h-3.5 text-amber-500" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(u)} data-testid={`delete-user-${u.user_id}`}>
                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Password Reset Dialog */}
      <Dialog open={!!resetDialog} onOpenChange={v => { if (!v) setResetDialog(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Key className="w-5 h-5 text-amber-500" /> Reset Password</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Reset password for <strong>{resetDialog?.email}</strong></p>
            <Input type="password" placeholder="New password" value={newPassword}
              onChange={e => setNewPassword(e.target.value)} data-testid="new-password-input" />
          </div>
          <DialogFooter>
            <Button onClick={handleResetPassword} disabled={saving} className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" data-testid="confirm-reset-pwd">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Key className="w-4 h-4 mr-2" />} Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editDialog} onOpenChange={v => { if (!v) setEditDialog(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Edit className="w-5 h-5 text-blue-500" /> Edit User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input value={editFields.name || ''} onChange={e => setEditFields(p => ({ ...p, name: e.target.value }))} data-testid="edit-user-name" /></div>
            <div><Label className="text-xs">Email</Label><Input value={editFields.email || ''} onChange={e => setEditFields(p => ({ ...p, email: e.target.value }))} data-testid="edit-user-email" /></div>
            <div><Label className="text-xs">Role</Label>
              <Select value={editFields.role || ''} onValueChange={v => setEditFields(p => ({ ...p, role: v }))}>
                <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['super_admin','admin','hr','principal','hod','finance','employee'].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Active</Label>
              <Select value={String(editFields.is_active)} onValueChange={v => setEditFields(p => ({ ...p, is_active: v === 'true' }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="true">Active</SelectItem><SelectItem value="false">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleEditUser} disabled={saving} className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" data-testid="save-edit-user">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />} Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={addDialog} onOpenChange={v => { if (!v) setAddDialog(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5 text-green-600" /> Add New User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Full Name *</Label><Input value={addFields.name} onChange={e => setAddFields(p => ({ ...p, name: e.target.value }))} placeholder="e.g. John Doe" data-testid="add-user-name" /></div>
            <div><Label className="text-xs">Email *</Label><Input type="email" value={addFields.email} onChange={e => setAddFields(p => ({ ...p, email: e.target.value }))} placeholder="e.g. john@sgi.edu" data-testid="add-user-email" /></div>
            <div><Label className="text-xs">Password *</Label><Input type="password" value={addFields.password} onChange={e => setAddFields(p => ({ ...p, password: e.target.value }))} placeholder="Min 4 characters" data-testid="add-user-password" /></div>
            <div><Label className="text-xs">Role</Label>
              <Select value={addFields.role} onValueChange={v => setAddFields(p => ({ ...p, role: v }))}>
                <SelectTrigger data-testid="add-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['super_admin','admin','hr','principal','hod','finance','employee'].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Institute</Label>
              <Select value={addFields.entity_id || 'none'} onValueChange={v => setAddFields(p => ({ ...p, entity_id: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="add-user-entity"><SelectValue placeholder="Select Institute" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {entities.map(e => <SelectItem key={e.entity_id} value={e.entity_id}>{e.name} ({e.code})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Department</Label><Input value={addFields.department} onChange={e => setAddFields(p => ({ ...p, department: e.target.value }))} placeholder="e.g. Computer Science" data-testid="add-user-department" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddUser} disabled={saving} className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" data-testid="save-add-user">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ====================================================================
   INSTITUTE / SUB-INSTITUTE MANAGEMENT
   ==================================================================== */
function InstituteManagement() {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [addDialog, setAddDialog] = useState(false);
  const [addFields, setAddFields] = useState({ name: '', type: '', code: '', compliance_body: '', parent_entity_id: '' });
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});

  const fetchEntities = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/entities`, { headers: h(), credentials: 'include' });
      if (res.ok) setEntities(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEntities(); }, [fetchEntities]);

  const parents = entities.filter(e => !e.parent_entity_id);
  const getChildren = (pid) => entities.filter(e => e.parent_entity_id === pid);

  const handleAdd = async () => {
    if (!addFields.name || !addFields.type || !addFields.code) { toast.error('Name, type, and code are required'); return; }
    setSaving(true);
    try {
      const body = { ...addFields };
      if (!body.parent_entity_id) delete body.parent_entity_id;
      const res = await fetch(`${API_URL}/api/entities`, {
        method: 'POST', headers: h(), credentials: 'include', body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      toast.success('Institute added'); setAddDialog(false);
      setAddFields({ name: '', type: '', code: '', compliance_body: '', parent_entity_id: '' });
      fetchEntities();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleEdit = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/records/entities/${editDialog.entity_id}`, {
        method: 'PUT', headers: h(), credentials: 'include', body: JSON.stringify(editFields),
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Institute updated'); setEditDialog(null); fetchEntities();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (entity) => {
    const children = getChildren(entity.entity_id);
    if (children.length > 0) { toast.error('Cannot delete — has sub-institutes. Delete sub-institutes first.'); return; }
    if (!window.confirm(`Delete institute "${entity.name}"? All associated data will be orphaned.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/records/entities/${entity.entity_id}`, { method: 'DELETE', headers: h(), credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      toast.success('Institute deleted'); fetchEntities();
    } catch (e) { toast.error(e.message); }
  };

  const openEdit = (e) => {
    setEditFields({ name: e.name, type: e.type, code: e.code, compliance_body: e.compliance_body || '', address: e.address || '', contact_email: e.contact_email || '', parent_entity_id: e.parent_entity_id || '' });
    setEditDialog(e);
  };

  const toggleExpand = (eid) => setExpanded(prev => ({ ...prev, [eid]: !prev[eid] }));

  const renderEntity = (entity, level = 0) => {
    const children = getChildren(entity.entity_id);
    const hasChildren = children.length > 0;
    return (
      <React.Fragment key={entity.entity_id}>
        <TableRow className="border-b border-slate-100 hover:bg-slate-50" data-testid={`entity-row-${entity.entity_id}`}>
          <TableCell className="text-sm" style={{ paddingLeft: `${16 + level * 24}px` }}>
            <div className="flex items-center gap-2">
              {hasChildren ? (
                <button onClick={() => toggleExpand(entity.entity_id)} className="text-slate-400 hover:text-slate-600">
                  {expanded[entity.entity_id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
              ) : <span className="w-4" />}
              <Building2 className={`w-4 h-4 ${level === 0 ? 'text-[#DC2626]' : 'text-slate-400'}`} />
              <span className="font-medium">{entity.name}</span>
              {entity.parent_entity_id && <Badge variant="outline" className="text-[10px]">Sub-Institute</Badge>}
            </div>
          </TableCell>
          <TableCell className="text-sm text-slate-600">{entity.type}</TableCell>
          <TableCell className="text-xs font-mono text-slate-500">{entity.code}</TableCell>
          <TableCell><Badge variant="outline" className="text-xs">{entity.compliance_body || '-'}</Badge></TableCell>
          <TableCell>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={() => openEdit(entity)} data-testid={`edit-entity-${entity.entity_id}`}>
                <Edit className="w-3.5 h-3.5 text-blue-500" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(entity)} data-testid={`delete-entity-${entity.entity_id}`}>
                <Trash2 className="w-3.5 h-3.5 text-red-500" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => { setAddFields(f => ({ ...f, parent_entity_id: entity.entity_id })); setAddDialog(true); }}
                title="Add Sub-Institute" data-testid={`add-sub-${entity.entity_id}`}>
                <Plus className="w-3.5 h-3.5 text-green-500" />
              </Button>
            </div>
          </TableCell>
        </TableRow>
        {hasChildren && expanded[entity.entity_id] && children.map(c => renderEntity(c, level + 1))}
      </React.Fragment>
    );
  };

  return (
    <div className="space-y-4" data-testid="institute-management-tab">
      <div className="flex justify-end">
        <Button onClick={() => { setAddFields({ name: '', type: '', code: '', compliance_body: '', parent_entity_id: '' }); setAddDialog(true); }}
          className="bg-[#DC2626] hover:bg-[#B91C1C] text-white gap-2" data-testid="add-institute-btn">
          <Plus className="w-4 h-4" /> Add Institute
        </Button>
      </div>

      <Card className="border border-slate-200 rounded-sm shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />)}</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs uppercase font-semibold text-slate-500">Institute Name</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500">Type</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500">Code</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500">Compliance</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 w-32">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>{parents.map(p => renderEntity(p))}</TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Institute Dialog */}
      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#DC2626]" />
              {addFields.parent_entity_id ? 'Add Sub-Institute' : 'Add Institute'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {addFields.parent_entity_id && (
              <div className="bg-blue-50 border border-blue-200 rounded p-2 text-sm text-blue-700">
                Sub-Institute under: <strong>{entities.find(e => e.entity_id === addFields.parent_entity_id)?.name}</strong>
              </div>
            )}
            <div><Label className="text-xs">Name</Label><Input value={addFields.name} onChange={e => setAddFields(p => ({ ...p, name: e.target.value }))} placeholder="Institute Name" data-testid="add-inst-name" /></div>
            <div><Label className="text-xs">Type</Label><Input value={addFields.type} onChange={e => setAddFields(p => ({ ...p, type: e.target.value }))} placeholder="e.g. Engineering, University" data-testid="add-inst-type" /></div>
            <div><Label className="text-xs">Code</Label><Input value={addFields.code} onChange={e => setAddFields(p => ({ ...p, code: e.target.value }))} placeholder="e.g. SGIEC" data-testid="add-inst-code" /></div>
            <div><Label className="text-xs">Compliance Body</Label><Input value={addFields.compliance_body} onChange={e => setAddFields(p => ({ ...p, compliance_body: e.target.value }))} placeholder="e.g. AICTE, UGC, CBSE" data-testid="add-inst-compliance" /></div>
          </div>
          <DialogFooter>
            <Button onClick={handleAdd} disabled={saving} className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" data-testid="save-add-inst">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Institute Dialog */}
      <Dialog open={!!editDialog} onOpenChange={v => { if (!v) setEditDialog(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Edit className="w-5 h-5 text-blue-500" /> Edit Institute</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input value={editFields.name || ''} onChange={e => setEditFields(p => ({ ...p, name: e.target.value }))} data-testid="edit-inst-name" /></div>
            <div><Label className="text-xs">Type</Label><Input value={editFields.type || ''} onChange={e => setEditFields(p => ({ ...p, type: e.target.value }))} /></div>
            <div><Label className="text-xs">Code</Label><Input value={editFields.code || ''} onChange={e => setEditFields(p => ({ ...p, code: e.target.value }))} /></div>
            <div><Label className="text-xs">Compliance Body</Label><Input value={editFields.compliance_body || ''} onChange={e => setEditFields(p => ({ ...p, compliance_body: e.target.value }))} /></div>
            <div><Label className="text-xs">Parent Institute</Label>
              <Select value={editFields.parent_entity_id || 'none'} onValueChange={v => setEditFields(p => ({ ...p, parent_entity_id: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (Top-level)</SelectItem>
                  {entities.filter(e => e.entity_id !== editDialog?.entity_id).map(e => <SelectItem key={e.entity_id} value={e.entity_id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleEdit} disabled={saving} className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" data-testid="save-edit-inst">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ====================================================================
   RECORDS BROWSER (universal edit/delete for any collection)
   ==================================================================== */
function RecordsBrowser() {
  const [collection, setCollection] = useState('employees');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [editDialog, setEditDialog] = useState(null);
  const [editJson, setEditJson] = useState('');
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState([]);

  const COLLECTIONS = [
    { key: 'employees', label: 'Employees', api: '/api/employees?is_active=true', idField: 'employee_id', display: r => `${r.first_name} ${r.last_name} (${r.department})` },
    { key: 'leave_requests', label: 'Leave Requests', api: '/api/leaves', idField: 'leave_id', display: r => `${r.employee_name || r.employee_id} - ${r.leave_type} (${r.status})` },
    { key: 'payroll', label: 'Payroll', api: '/api/payroll', idField: 'payroll_id', display: r => `${r.employee_name || r.employee_id} - ${r.month} (${r.status})` },
    { key: 'lnd_activities', label: 'L&D Activities', api: '/api/lnd/activities/entity', idField: 'activity_id', display: r => `${r.employee_name || r.employee_id} - ${r.category} (${r.status})` },
    { key: 'salary_revisions', label: 'Salary Revisions', api: '/api/salary-revisions', idField: 'revision_id', display: r => `${r.employee_name} - ${r.status}` },
    { key: 'attendance', label: 'Attendance', api: '/api/attendance/my', idField: 'record_id', display: r => `${r.employee_id} - ${r.date} (${r.status})` },
  ];

  const current = COLLECTIONS.find(c => c.key === collection) || COLLECTIONS[0];

  const fetchRecords = useCallback(async () => {
    setLoading(true); setSelected([]);
    try {
      const res = await fetch(`${API_URL}${current.api}`, { headers: h(), credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setRecords(Array.isArray(data) ? data : data.logs || data.records || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [current.api]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const handleDelete = async (record) => {
    const rid = record[current.idField];
    if (!window.confirm(`Delete ${current.label} record ${rid}?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/records/${collection}/${rid}`, { method: 'DELETE', headers: h(), credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      toast.success('Record deleted'); fetchRecords();
    } catch (e) { toast.error(e.message); }
  };

  const handleEdit = async () => {
    setSaving(true);
    try {
      const body = JSON.parse(editJson);
      const rid = editDialog[current.idField];
      const res = await fetch(`${API_URL}/api/admin/records/${collection}/${rid}`, {
        method: 'PUT', headers: h(), credentials: 'include', body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Record updated'); setEditDialog(null); fetchRecords();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.length} records from ${current.label}?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/bulk-delete`, {
        method: 'POST', headers: h(), credentials: 'include',
        body: JSON.stringify({ collection, record_ids: selected }),
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      toast.success(`Deleted ${data.deleted_count} records`); setSelected([]); fetchRecords();
    } catch (e) { toast.error(e.message); }
  };

  const openEdit = (record) => {
    const clean = { ...record };
    delete clean._id;
    setEditJson(JSON.stringify(clean, null, 2));
    setEditDialog(record);
  };

  const toggleSelect = (rid) => setSelected(prev => prev.includes(rid) ? prev.filter(id => id !== rid) : [...prev, rid]);

  const filtered = records.filter(r => {
    if (!search) return true;
    return JSON.stringify(r).toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="space-y-4" data-testid="records-browser-tab">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
        <div>
          <Label className="text-xs text-slate-500">Collection</Label>
          <Select value={collection} onValueChange={v => { setCollection(v); setSearch(''); setSelected([]); }}>
            <SelectTrigger className="w-52 rounded-sm" data-testid="collection-select"><SelectValue /></SelectTrigger>
            <SelectContent>{COLLECTIONS.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search records..." className="pl-10 rounded-sm" value={search} onChange={e => setSearch(e.target.value)} data-testid="records-search" />
        </div>
        {selected.length > 0 && (
          <Button variant="destructive" size="sm" onClick={handleBulkDelete} data-testid="bulk-delete-records-btn">
            <Trash2 className="w-3 h-3 mr-1" /> Delete {selected.length}
          </Button>
        )}
      </div>

      <Card className="border border-slate-200 rounded-sm shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />)}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-400"><Database className="w-10 h-10 mx-auto mb-2 opacity-40" /><p>No records found</p></div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-10"><input type="checkbox" checked={selected.length === filtered.length && filtered.length > 0} onChange={() => setSelected(p => p.length === filtered.length ? [] : filtered.map(r => r[current.idField]))} /></TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500">ID</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500">Summary</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.slice(0, 100).map(r => {
                    const rid = r[current.idField];
                    return (
                      <TableRow key={rid} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`record-row-${rid}`}>
                        <TableCell><input type="checkbox" checked={selected.includes(rid)} onChange={() => toggleSelect(rid)} /></TableCell>
                        <TableCell className="text-xs font-mono text-slate-400">{rid}</TableCell>
                        <TableCell className="text-sm">{current.display(r)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`edit-record-${rid}`}>
                              <Edit className="w-3.5 h-3.5 text-blue-500" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(r)} data-testid={`delete-record-${rid}`}>
                              <Trash2 className="w-3.5 h-3.5 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Record (JSON) Dialog */}
      <Dialog open={!!editDialog} onOpenChange={v => { if (!v) setEditDialog(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Edit className="w-5 h-5 text-blue-500" /> Edit Record</DialogTitle></DialogHeader>
          <Textarea value={editJson} onChange={e => setEditJson(e.target.value)} rows={18} className="font-mono text-xs rounded-sm" data-testid="edit-record-json" />
          <DialogFooter>
            <Button onClick={handleEdit} disabled={saving} className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" data-testid="save-edit-record">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ====================================================================
   FACE REGISTRATION MANAGEMENT
   ==================================================================== */
function FaceRegistrationManagement() {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected
  const [entityFilter, setEntityFilter] = useState('all');
  const [entities, setEntities] = useState([]);
  const [stats, setStats] = useState({ total: 0, registered: 0, pending: 0, unregistered: 0 });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [regRes, entRes] = await Promise.all([
        fetch(`${API_URL}/api/face/registrations`, { headers: h(), credentials: 'include' }),
        fetch(`${API_URL}/api/entities`, { headers: h(), credentials: 'include' })
      ]);
      if (regRes.ok) {
        const regs = await regRes.json();
        setRegistrations(regs);
        const approved = regs.filter(r => r.status === 'approved').length;
        const pending = regs.filter(r => r.status === 'pending').length;
        setStats({ total: regs.length, registered: approved, pending, unregistered: 0 });
      }
      if (entRes.ok) setEntities(await entRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleApprove = async (regId) => {
    try {
      const res = await fetch(`${API_URL}/api/face/approve`, {
        method: 'POST', headers: h(), credentials: 'include',
        body: JSON.stringify({ registration_id: regId, action: 'approve' })
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Face registration approved');
      fetchData();
    } catch (e) { toast.error(e.message); }
  };

  const handleReject = async (regId) => {
    if (!window.confirm('Reject this face registration?')) return;
    try {
      const res = await fetch(`${API_URL}/api/face/approve`, {
        method: 'POST', headers: h(), credentials: 'include',
        body: JSON.stringify({ registration_id: regId, action: 'reject' })
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Face registration rejected');
      fetchData();
    } catch (e) { toast.error(e.message); }
  };

  const handleDelete = async (empId) => {
    if (!window.confirm('Delete face registration? Employee will need to re-register.')) return;
    try {
      const res = await fetch(`${API_URL}/api/face/registration/${empId}`, {
        method: 'DELETE', headers: h(), credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Face registration deleted');
      fetchData();
    } catch (e) { toast.error(e.message); }
  };

  const filtered = registrations.filter(r => {
    const matchStatus = filter === 'all' || r.status === filter;
    const matchEntity = entityFilter === 'all' || r.entity_id === entityFilter;
    return matchStatus && matchEntity;
  });

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4" data-testid="face-management-tab">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="stat-card">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Total Registrations</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Camera className="w-6 h-6 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Approved</p>
                <p className="text-2xl font-bold text-green-600">{stats.registered}</p>
              </div>
              <ShieldCheck className="w-6 h-6 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Pending Approval</p>
                <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
              </div>
              <AlertTriangle className="w-6 h-6 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Rejected</p>
                <p className="text-2xl font-bold text-red-600">{registrations.filter(r => r.status === 'rejected').length}</p>
              </div>
              <UserX className="w-6 h-6 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[160px]" data-testid="face-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Select value={entityFilter} onValueChange={setEntityFilter}>
          <SelectTrigger className="w-[200px]" data-testid="face-entity-filter">
            <Building2 className="w-4 h-4 mr-1 text-slate-400" />
            <SelectValue placeholder="All Institutes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Institutes</SelectItem>
            {entities.map(ent => (
              <SelectItem key={ent.entity_id} value={ent.entity_id}>{ent.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={fetchData} className="ml-auto">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Registered On</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">No registrations found</TableCell></TableRow>
                ) : (
                  filtered.map(reg => (
                    <TableRow key={reg.registration_id} data-testid={`face-reg-${reg.registration_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {reg.face_snapshot ? (
                            <img src={reg.face_snapshot} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-slate-200" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                              <Camera className="w-5 h-5 text-slate-400" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-sm">{reg.employee_name || reg.employee_id}</p>
                            <p className="text-xs text-slate-400">{reg.employee_id}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{reg.department || '-'}</TableCell>
                      <TableCell>
                        <Badge className={
                          reg.status === 'approved' ? 'bg-green-100 text-green-800' :
                          reg.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                          'bg-red-100 text-red-800'
                        }>
                          {reg.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {reg.created_at ? new Date(reg.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          {reg.status === 'pending' && (
                            <>
                              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white h-7 text-xs" onClick={() => handleApprove(reg.registration_id)} data-testid={`approve-${reg.registration_id}`}>
                                <Check className="w-3 h-3 mr-1" /> Approve
                              </Button>
                              <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => handleReject(reg.registration_id)} data-testid={`reject-${reg.registration_id}`}>
                                <X className="w-3 h-3 mr-1" /> Reject
                              </Button>
                            </>
                          )}
                          {reg.status === 'approved' && (
                            <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200" onClick={() => handleDelete(reg.employee_id)} data-testid={`delete-face-${reg.employee_id}`}>
                              <Trash2 className="w-3 h-3 mr-1" /> Remove
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


/* ====================================================================
   MAIN ADMIN PANEL PAGE
   ==================================================================== */
export default function AdminPanel() {
  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-panel-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
          <Shield className="w-7 h-7 text-[#DC2626]" /> Super Admin Panel
        </h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm">
          Full control over all system data, users, institutes, and records
        </p>
      </div>

      <Tabs defaultValue="directory" className="w-full">
        <div className="overflow-x-auto">
        <TabsList className="grid w-full min-w-[800px] md:min-w-0 grid-cols-8">
          <TabsTrigger value="directory" data-testid="tab-directory" className="flex items-center gap-1.5">
            <Users className="w-4 h-4" /> Directory
          </TabsTrigger>
          <TabsTrigger value="roles" data-testid="tab-roles" className="flex items-center gap-1.5">
            <Shield className="w-4 h-4" /> Roles
          </TabsTrigger>
          <TabsTrigger value="face" data-testid="tab-face" className="flex items-center gap-1.5">
            <Camera className="w-4 h-4" /> Face ID
          </TabsTrigger>
          <TabsTrigger value="corrections" data-testid="tab-corrections" className="flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4" /> Corrections
          </TabsTrigger>
          <TabsTrigger value="departments" data-testid="tab-departments" className="flex items-center gap-1.5">
            <Building2 className="w-4 h-4" /> Departments
          </TabsTrigger>
          <TabsTrigger value="integrity" data-testid="tab-integrity" className="flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4" /> Data Integrity
          </TabsTrigger>
          <TabsTrigger value="data" data-testid="tab-data" className="flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4" /> Data Mgmt
          </TabsTrigger>
          <TabsTrigger value="system" data-testid="tab-system" className="flex items-center gap-1.5">
            <Settings className="w-4 h-4" /> System
          </TabsTrigger>
        </TabsList>
        </div>

        <TabsContent value="directory" className="mt-6"><MasterDirectory /></TabsContent>
        <TabsContent value="roles" className="mt-6"><RoleManagement embedded /></TabsContent>
        <TabsContent value="face" className="mt-6"><FaceRegistrationManagement /></TabsContent>
        <TabsContent value="corrections" className="mt-6"><CorrectionRequestsAdmin isSuperAdmin={true} /></TabsContent>
        <TabsContent value="departments" className="mt-6"><DepartmentsManager /></TabsContent>
        <TabsContent value="integrity" className="mt-6"><DataIntegrity embedded /></TabsContent>
        <TabsContent value="data" className="mt-6"><DataManagement /></TabsContent>
        <TabsContent value="system" className="mt-6"><SystemConfig /></TabsContent>
      </Tabs>
    </div>
  );
}
